// @flow
import React from 'react';
import { withStyles, withTheme } from '@material-ui/core/styles';
import platform from 'platform';
import keycodes from '../../utils/keycodes';
import {
  setEndOfContenteditable,
  selectElementContents,
  setStartOfContenteditable,
  getCaretPosition,
} from '../../utils/domUtils';
import { removeEndPunctuation, endPunctuation, capitalizeWord, toggleCapitalizeWord } from '../../utils/scribeUtils';

const styles = {
  blocInput: {
    display: 'inline',
    padding: '1px',
    outline: 'none',
    cursor: 'pointer',
    whiteSpace: 'pre-wrap',
    '&::selection': {
      background: 'transparent',
    },
  },
};

type Props = {
  conversationEnded: boolean,
  highlightTranscript: Function,
  insertUpdateMutation: Function,
  scribeDeselectTranscriptWord: Function,
  scribeJumpToBlocEnd: Function,
  scribeJumpToBlocStart: Function,
  scribeJumpToNextBloc: Function,
  scribeJumpToNextHightlightedBloc: Function,
  scribeJumpToPreviousBloc: Function,
  scribeMultiSelectLeft: Function,
  scribeMultiSelectRight: Function,
  scribeSelectTranscriptWord: Function,
  scribeUpdateInputValue: Function,
  scribeWordEditSetStarted: Function,
  selectedTranscriptCaretPosition: string,
  selectedTranscriptIndex: number,
  selectedWordIndex: number,
  selectedWordInputValue: string,
  theme: Object,
  wordBeingEdited: boolean,
  classes: Object,
};

type State = {
  noInteractionYet: boolean,
  noInputChangeYet: boolean,
};

const isMac = platform.os.family === 'OS X';

class ScribeWordInput extends React.Component<Props, State> {
  $input: HTMLElement;

  textSet: ?boolean;

  constructor() {
    super();
    this.state = {
      noInteractionYet: true,
      noInputChangeYet: true,
    };
  }

  componentWillReceiveProps(nextProps) {
    if (this.$input.innerText === '' && nextProps.selectedWordInputValue.trim() !== '') {
      this.$input.innerText = nextProps.selectedWordInputValue.replace(/`/g, '');
      selectElementContents(this.$input);
    }
  }

  setupInput(el: ?HTMLDivElement) {
    if (!el) return;

    this.$input = el;

    if (el.innerText === '' && !this.textSet) {
      this.textSet = true;
      // eslint-disable-next-line
      el.innerText = this.props.selectedWordInputValue.replace(/`/g, '');
    }
  }

  componentDidMount() {
    const { conversationEnded, wordBeingEdited, selectedTranscriptCaretPosition } = this.props;
    if (this.$input && !conversationEnded) {
      setEndOfContenteditable(this.$input);

      if (!wordBeingEdited && selectedTranscriptCaretPosition === 'all') {
        selectElementContents(this.$input);
      }

      if (selectedTranscriptCaretPosition === 'first') {
        setStartOfContenteditable(this.$input);
      }

      // this.$input.scrollIntoView();
    }
  }

  handleClick = () => {
    this.setState({ noInteractionYet: false });
  };

  handleKeyDown = (ev: any) => {
    const key = keycodes(ev);
    const { $input } = this;

    if (!$input) return;

    const {
      highlightTranscript,
      insertUpdateMutation,
      scribeJumpToBlocEnd,
      scribeJumpToBlocStart,
      scribeJumpToNextBloc,
      scribeJumpToNextHightlightedBloc,
      scribeJumpToPreviousBloc,
      scribeMultiSelectLeft,
      scribeMultiSelectRight,
      scribeSelectTranscriptWord,
      scribeUpdateInputValue,
      selectedTranscriptIndex,
      selectedWordIndex,
    } = this.props;

    const { noInputChangeYet, noInteractionYet } = this.state;

    if (key.RETURN && !key.CTRL && !key.META) {
      ev.preventDefault();
      ev.stopPropagation();
      scribeSelectTranscriptWord(
        {
          index: selectedWordIndex,
          indexTranscript: selectedTranscriptIndex,
          direction: 1,
          skipCreateNextBloc: false,
        },
        { src: 'RETURN_KEYPRESS' }
      );
    } else if (key.CTRL && key.SHIFT && key.LEFT_ARROW) {
      scribeJumpToBlocStart();
    } else if ((!isMac && key.CTRL && key.ALT && key.DOWN_ARROW) || (isMac && key.META && key.DOWN_ARROW)) {
      scribeJumpToNextHightlightedBloc({
        transcriptId: selectedTranscriptIndex,
        direction: 1,
      });
    } else if ((!isMac && key.CTRL && key.ALT && key.UP_ARROW) || (isMac && key.META && key.UP_ARROW)) {
      scribeJumpToNextHightlightedBloc({
        transcriptId: selectedTranscriptIndex,
        direction: -1,
      });
    } else if (key.CTRL && key.SHIFT && key.RIGHT_ARROW) {
      scribeJumpToBlocEnd();
    } else if (key.LEFT_ARROW && key.SHIFT && noInputChangeYet) {
      scribeMultiSelectLeft();
    } else if (key.RIGHT_ARROW && key.SHIFT && noInputChangeYet) {
      scribeMultiSelectRight();
    } else if (key.ALT && key.UP_ARROW) {
      scribeJumpToPreviousBloc({
        indexTranscript: selectedTranscriptIndex,
        firstWord: true,
      });
    } else if (key.ALT && key.DOWN_ARROW) {
      scribeJumpToNextBloc({
        transcriptId: selectedTranscriptIndex,
        skipCreateNextBloc: true,
      });
    } else if ((!isMac && key.CTRL && key.ALT && key.LEFT_ARROW) || (isMac && key.META && key.LEFT_ARROW)) {
      scribeJumpToBlocStart();
    } else if ((!isMac && key.CTRL && key.ALT && key.RIGHT_ARROW) || (isMac && key.META && key.RIGHT_ARROW)) {
      scribeJumpToBlocEnd();
    } else if ((!isMac && key.CTRL && key.h) || (isMac && key.META && key.h)) {
      ev.stopPropagation();
      ev.preventDefault();
      ev.nativeEvent.stopImmediatePropagation();
      highlightTranscript(selectedTranscriptIndex);
    } else if ((!isMac && key.CTRL && key.BACKSPACE) || (isMac && key.META && key.BACKSPACE)) {
      // Do nothing here
    } else if (key.BACKSPACE) {
      // This is already "handled" by handleChange and we dont want it to bubble up to the transcript level
      ev.stopPropagation();
      ev.nativeEvent.stopImmediatePropagation();
    } else if ((!isMac && key.CTRL && ev.key === 'b') || (isMac && key.META && ev.key === 'b')) {
      ev.preventDefault();
      ev.stopPropagation();
      $input.innerText = capitalizeWord($input.innerText || '');
      scribeUpdateInputValue($input.innerText || '', 'all');
      selectElementContents(this.$input);
      insertUpdateMutation({
        selectedWordIndex,
        selectedTranscriptIndex,
        text: $input.innerText,
        src: `press_"${ev.key}"`,
      });
    } else if (endPunctuation.includes(ev.key) && noInputChangeYet && $input.innerText !== '') {
      ev.preventDefault();
      ev.stopPropagation();
      $input.innerText = `${removeEndPunctuation($input.innerText || '', ev.key)}${ev.key}`;
      scribeUpdateInputValue($input.innerText || '', 'all');
      selectElementContents(this.$input);
      insertUpdateMutation({
        selectedWordIndex,
        selectedTranscriptIndex,
        text: $input.innerText,
        src: `press_"${ev.key}"`,
      });
    } else if (ev.key === ' ' && noInputChangeYet && noInteractionYet) {
      ev.preventDefault();
      ev.stopPropagation();
      $input.innerText = `${$input.innerText || ''} `;
      scribeUpdateInputValue($input.innerText || '', 'all');
      selectElementContents(this.$input);
      insertUpdateMutation({
        selectedWordIndex,
        selectedTranscriptIndex,
        text: $input.innerText,
        src: `press_space`,
      });
    } else if ((!isMac && key.CTRL && ev.key === 'c') || (isMac && key.META && ev.key === 'c')) {
      ev.preventDefault();
      ev.stopPropagation();
      $input.innerText = `${toggleCapitalizeWord($input.innerText || '')}`;
      scribeUpdateInputValue($input.innerText || '', 'all');
      selectElementContents(this.$input);
      insertUpdateMutation({
        selectedWordIndex,
        selectedTranscriptIndex,
        text: $input.innerText,
        src: `press_"${ev.key}"`,
      });
      scribeSelectTranscriptWord(
        {
          index: selectedWordIndex,
          indexTranscript: selectedTranscriptIndex,
          direction: 1,
          skipCreateNextBloc: false,
        },
        { src: 'CAPITALIZE' }
      );
    } else if ((!isMac && key.CTRL && ev.key === 'd') || (isMac && key.META && ev.key === 'd')) {
      ev.preventDefault();
      ev.stopPropagation();
      $input.innerText = `${removeEndPunctuation($input.innerText || '')}`;
      scribeUpdateInputValue($input.innerText || '', 'all');
      selectElementContents(this.$input);
      insertUpdateMutation({
        selectedWordIndex,
        selectedTranscriptIndex,
        text: $input.innerText,
        forceUncapitalize: false,
        src: `press_"${ev.key}"`,
      });
    }

    this.setState({ noInteractionYet: false });
  };

  handleChange = () => {
    const {
      insertUpdateMutation,
      scribeDeselectTranscriptWord,
      scribeSelectTranscriptWord,
      scribeUpdateInputValue,
      scribeWordEditSetStarted,
      selectedTranscriptIndex,
      selectedWordIndex,
    } = this.props;

    const { noInputChangeYet } = this.state;
    if (noInputChangeYet) {
      scribeWordEditSetStarted({
        indexTranscript: selectedTranscriptIndex,
        index: selectedWordIndex,
      });
    }
    this.setState({ noInputChangeYet: false });
    const { $input } = this;
    if (!$input) return;

    const text = !$input.innerText || $input.innerText === '\n' ? '' : $input.innerText;

    scribeUpdateInputValue(text);
    if (text === '') {
      scribeDeselectTranscriptWord({}, { src: 'BACKSPACE_KEYPRESS' });
      scribeSelectTranscriptWord({
        index: selectedWordIndex,
        indexTranscript: selectedTranscriptIndex,
        skipCommit: true,
        skipJumpNextBloc: true,
      });
    }

    // TODO: remove the must include space condition
    // to have letter by letter mutation
    // Only when backend (ot) will be readu
    if (text.includes(' ')) {
      insertUpdateMutation({
        selectedWordIndex,
        selectedTranscriptIndex,
        text,
        src: 'SPACE_KEYPRESS',
      });
    }
  };

  render() {
    const { theme, classes } = this.props;
    return (
      <span>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          className={classes.blocInput}
          style={{
            background: theme.palette.secondary[theme.palette.type === 'light' ? 'dark' : 'light'],
            color: theme.palette.primary[theme.palette.type],
          }}
          onClick={this.handleClick}
          ref={el => {
            this.setupInput(el);
          }}
          contentEditable="true"
          suppressContentEditableWarning="true"
          onInput={this.handleChange}
          onKeyDown={this.handleKeyDown}
        />{' '}
      </span>
    );
  }
}

export default withStyles(styles)(withTheme(ScribeWordInput));
